import React, { useEffect, useState, useCallback, useRef } from "react";
import EventsService from "../services/EventsService";

const PRESET_COLORS = [
  "#0A84FF", "#30D158", "#FF453A", "#FFD60A",
  "#BF5AF2", "#FF9F0A", "#64D2FF", "#FF375F",
  "#AC8E68", "#5E5CE6",
];

const PRESET_ICONS = [
  "fa-bolt", "fa-coffee", "fa-dumbbell", "fa-heart",
  "fa-water", "fa-smoking", "fa-pills", "fa-running",
  "fa-book", "fa-moon", "fa-utensils", "fa-brain",
  "fa-star", "fa-check", "fa-flag", "fa-fire",
];

function formatTimeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const S = {
  subtitle: {
    color: "rgba(255,255,255,0.4)", fontSize: 13, marginBottom: 24,
  },
  addBtn: {
    background: "linear-gradient(135deg, #0A84FF, #0066CC)",
    color: "#fff", border: "none", borderRadius: 12,
    padding: "10px 20px", fontWeight: 600, fontSize: 14,
    cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8,
    marginBottom: 28,
  },
  grid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 16,
  },
  card: (color, isSelected) => ({
    background: "rgba(255,255,255,0.04)", backdropFilter: "blur(12px)",
    border: `1px solid ${isSelected ? color : "rgba(255,255,255,0.08)"}`,
    borderRadius: 16, overflow: "hidden", transition: "border-color 0.2s",
  }),
  cardMain: {
    padding: "16px 16px 12px", cursor: "pointer", position: "relative",
  },
  iconCircle: (color) => ({
    width: 44, height: 44, borderRadius: 12,
    background: `${color}33`, display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 20, color, flexShrink: 0,
  }),
  cardName: {
    fontWeight: 600, fontSize: 15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
  },
  cardHint: { fontSize: 11, color: "rgba(255,255,255,0.4)", marginTop: 2 },
  cardActions: {
    display: "flex", borderTop: "1px solid rgba(255,255,255,0.08)",
  },
  cardBtn: (isActive, color) => ({
    flex: 1, padding: "8px 0", fontSize: 11,
    background: "transparent", border: "none",
    color: isActive ? color : "rgba(255,255,255,0.4)",
    cursor: "pointer", fontWeight: 500,
  }),
  cardBtnBorder: {
    borderLeft: "1px solid rgba(255,255,255,0.08)",
  },
  pulse: (color) => ({
    position: "absolute", inset: 0, background: `${color}22`,
    animation: "pulse-fade 0.4s ease-out", pointerEvents: "none",
  }),
  formOverlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 2000,
  },
  formCard: {
    background: "rgba(18,18,32,0.97)", backdropFilter: "blur(20px)",
    border: "0.5px solid rgba(255,255,255,0.12)", borderRadius: 20,
    padding: 24, width: "90%", maxWidth: 440, maxHeight: "85vh", overflowY: "auto",
  },
  formTitle: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    marginBottom: 20,
  },
  formCloseBtn: {
    background: "rgba(255,255,255,0.08)", border: "none", borderRadius: 8,
    width: 32, height: 32, color: "rgba(255,255,255,0.5)", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
  },
  formSubmitBtn: {
    background: "linear-gradient(135deg, #5eaa46, #4a9438)",
    color: "#fff", border: "none", borderRadius: 14,
    padding: "12px 0", fontWeight: 700, fontSize: 15,
    cursor: "pointer", width: "100%", marginTop: 8,
  },
  empty: { textAlign: "center", padding: "60px 20px" },
  emptyIcon: { fontSize: 48, color: "rgba(255,255,255,0.08)", marginBottom: 16 },
  emptyText: { color: "rgba(255,255,255,0.3)", fontSize: 14, fontWeight: 500 },
  emptyHint: { color: "rgba(255,255,255,0.18)", fontSize: 12, marginTop: 6 },
  historyCard: {
    background: "rgba(255,255,255,0.04)", backdropFilter: "blur(12px)",
    border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 16,
    padding: "16px 20px", marginTop: 24,
  },
};

function QuickActionsPage() {
  const [actions, setActions] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [icon, setIcon] = useState("fa-bolt");
  const [color, setColor] = useState("#0A84FF");
  const [editingId, setEditingId] = useState(null);
  const [loggingId, setLoggingId] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);
  const [showLogs, setShowLogs] = useState(false);
  const pulseTimers = useRef({});

  const fetchActions = useCallback(async () => {
    try {
      const data = await EventsService.listPersistentActions();
      setActions(data || []);
    } catch {
      setActions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLogs = useCallback(async (actionId) => {
    try {
      const data = await EventsService.getPersistentLogs(actionId);
      setLogs(data || []);
    } catch {
      setLogs([]);
    }
  }, []);

  useEffect(() => { fetchActions(); }, [fetchActions]);

  const resetForm = () => {
    setName(""); setIcon("fa-bolt"); setColor("#0A84FF");
    setEditingId(null); setShowForm(false);
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    try {
      if (editingId) await EventsService.deletePersistentAction(editingId);
      await EventsService.createPersistentAction({ name: name.trim(), icon, color });
      resetForm(); fetchActions();
    } catch (err) { console.error("Failed to save:", err); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this quick action and all its logs?")) return;
    try {
      await EventsService.deletePersistentAction(id);
      fetchActions();
      if (selectedAction === id) { setSelectedAction(null); setShowLogs(false); }
    } catch (err) { console.error("Delete failed:", err); }
  };

  const handleLog = async (action) => {
    setLoggingId(action.id);
    try {
      await EventsService.logPersistentAction(action.id);
      if (selectedAction === action.id) fetchLogs(action.id);
    } catch (err) { console.error("Log failed:", err); }
    if (pulseTimers.current[action.id]) clearTimeout(pulseTimers.current[action.id]);
    pulseTimers.current[action.id] = setTimeout(() => setLoggingId(null), 400);
  };

  const handleViewLogs = async (action) => {
    if (selectedAction === action.id && showLogs) {
      setShowLogs(false); setSelectedAction(null); return;
    }
    setSelectedAction(action.id); setShowLogs(true); fetchLogs(action.id);
  };

  const handleEdit = (action) => {
    setEditingId(action.id); setName(action.name);
    setIcon(action.icon || "fa-bolt"); setColor(action.color || "#0A84FF");
    setShowForm(true);
  };

  return (
    <>
      {/* Page Header — same pattern as all other pages */}
      <div className="page-header">
        <nav className="navbar navbar-expand-lg d-flex justify-content-between">
          <div className="header-title flex-fill">
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              id="sidebar-toggle"
              onClick={(e) => { e.preventDefault(); window.__toggleSidebar && window.__toggleSidebar(e); }}
            >
              <i className="fas fa-bars"></i>
            </a>
            <h5>
              <i className="fas fa-bolt" style={{ marginRight: 8 }}></i>
              Quick Actions
            </h5>
          </div>
        </nav>
      </div>

      <div className="main-wrapper">
        <div className="create-event-container">
          <p style={S.subtitle}>
            Track anything with a single tap. Not tied to any activity.
          </p>

          <button style={S.addBtn} onClick={() => { resetForm(); setShowForm(true); }}>
            <i className="fas fa-plus"></i>
            New Action
          </button>

          {/* Modal Form — same overlay pattern as AlarmsPage */}
          {showForm && (
            <div style={S.formOverlay} onClick={(e) => { if (e.target === e.currentTarget) resetForm(); }}>
              <div style={S.formCard}>
                <div style={S.formTitle}>
                  <span style={{ color: "#fff", fontSize: 18, fontWeight: 700 }}>
                    {editingId ? "Edit Quick Action" : "New Quick Action"}
                  </span>
                  <button style={S.formCloseBtn} onClick={resetForm}>
                    <i className="fas fa-times"></i>
                  </button>
                </div>

                <label className="create-event-label">Name</label>
                <input
                  type="text"
                  className="form-control create-event-name-input"
                  placeholder="e.g. Water, Coffee, Push-ups..."
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                  style={{ marginBottom: 16 }}
                />

                <label className="create-event-label">Icon</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
                  {PRESET_ICONS.map((ic) => (
                    <button
                      key={ic}
                      className={`device-button${icon === ic ? " device-button-selected" : ""}`}
                      onClick={() => setIcon(ic)}
                      style={{
                        width: 40, height: 40, aspectRatio: "auto",
                        maxWidth: 40, borderRadius: 10,
                        borderStyle: "solid",
                        borderWidth: icon === ic ? 2 : 1,
                        borderColor: icon === ic ? "#0A84FF" : "rgba(255,255,255,0.15)",
                        background: icon === ic ? "rgba(10,132,255,0.2)" : "rgba(255,255,255,0.06)",
                        color: "#fff", fontSize: 14, cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        padding: 0, boxShadow: "none",
                      }}
                    >
                      <i className={`fas ${ic}`} />
                    </button>
                  ))}
                </div>

                <label className="create-event-label">Color</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
                  {PRESET_COLORS.map((c) => (
                    <button
                      key={c}
                      onClick={() => setColor(c)}
                      style={{
                        width: 32, height: 32, borderRadius: "50%",
                        background: c, cursor: "pointer", border: "none",
                        outline: color === c ? "3px solid #fff" : "2px solid transparent",
                        outlineOffset: 2,
                        boxShadow: color === c ? `0 0 10px ${c}` : "none",
                        transition: "outline 0.15s, box-shadow 0.15s",
                      }}
                    />
                  ))}
                </div>

                <button style={S.formSubmitBtn} onClick={handleSave}>
                  <i className="fas fa-check" style={{ marginRight: 8 }}></i>
                  {editingId ? "Save Changes" : "Create Action"}
                </button>
              </div>
            </div>
          )}

          {/* Grid */}
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : actions.length === 0 ? (
            <div style={S.empty}>
              <div style={S.emptyIcon}><i className="fas fa-bolt"></i></div>
              <div style={S.emptyText}>No quick actions yet</div>
              <div style={S.emptyHint}>Create one to start tracking anything with a single tap</div>
            </div>
          ) : (
            <div style={S.grid}>
              {actions.map((action) => {
                const ac = action.color || "#0A84FF";
                const isSelected = selectedAction === action.id && showLogs;
                return (
                  <div key={action.id} style={S.card(ac, isSelected)}>
                    <div style={S.cardMain} onClick={() => handleLog(action)}>
                      {loggingId === action.id && <div style={S.pulse(ac)} />}
                      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                        <div style={S.iconCircle(ac)}>
                          <i className={`fas ${action.icon || "fa-bolt"}`} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={S.cardName}>{action.name}</div>
                          <div style={S.cardHint}>Tap to log</div>
                        </div>
                      </div>
                    </div>
                    <div style={S.cardActions}>
                      <button style={S.cardBtn(isSelected, ac)} onClick={() => handleViewLogs(action)}>
                        <i className="fas fa-history" style={{ marginRight: 4 }}></i>
                        History
                      </button>
                      <button style={{ ...S.cardBtn(false, ac), ...S.cardBtnBorder }} onClick={() => handleEdit(action)}>
                        <i className="fas fa-pen" style={{ marginRight: 4 }}></i>
                        Edit
                      </button>
                      <button style={{ ...S.cardBtn(false, ac), ...S.cardBtnBorder }} onClick={() => handleDelete(action.id)}>
                        <i className="fas fa-trash" style={{ marginRight: 4 }}></i>
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* History Panel */}
          {showLogs && selectedAction && (
            <div style={S.historyCard}>
              <h6 className="event-section-title" style={{ marginBottom: 14 }}>
                <i className="fas fa-history" style={{ marginRight: 8, color: actions.find((a) => a.id === selectedAction)?.color || "#0A84FF" }}></i>
                {actions.find((a) => a.id === selectedAction)?.name} — History
              </h6>
              {logs.length === 0 ? (
                <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 13, margin: 0 }}>
                  No logs yet. Tap the action to start tracking!
                </p>
              ) : (
                <div style={{ maxHeight: 320, overflowY: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ color: "rgba(255,255,255,0.5)", textAlign: "left", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                        <th style={{ padding: "8px 12px", fontWeight: 500 }}>#</th>
                        <th style={{ padding: "8px 12px", fontWeight: 500 }}>Time</th>
                        <th style={{ padding: "8px 12px", fontWeight: 500 }}>Ago</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log, i) => (
                        <tr key={log.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                          <td style={{ padding: "8px 12px", color: "rgba(255,255,255,0.3)" }}>{i + 1}</td>
                          <td style={{ padding: "8px 12px" }}>{new Date(log.time).toLocaleString()}</td>
                          <td style={{ padding: "8px 12px", color: "rgba(255,255,255,0.4)" }}>{formatTimeAgo(log.time)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pulse-fade {
          0% { opacity: 1; }
          100% { opacity: 0; }
        }
      `}</style>
    </>
  );
}

export default QuickActionsPage;
